/* eslint-disable no-unused-vars */
import { useCallback, useEffect, useMemo, useRef, useState, useContext } from 'react'
import { useLocation, useNavigate, useMatch } from 'react-router-dom'
import { useGetOpeningStoryDetail } from 'src/pages/newUser/hooks/useGetOpeningStoryDetail'
import { useGetFirstStory } from 'src/pages/newUser/hooks/useGetFirstStory'
import { MAIN_PATHS } from 'src/routes/main'
import { postReadOpeningStory } from 'src/services/post/postSkipOpeningStory'
import { HistoryRouteContext } from 'src/routes'
import { SIDE_STORY_PATHS } from 'src/routes/sidestory'
import { useGetSideStoryDetail } from 'src/pages/menu/hooks/useGetSideStoryDetail'
export const useGetStoryDetail = (id) => {
  const startTime = useRef(Date.now())
  const [isReady, setIsReady] = useState(false)
  const match = useMatch(SIDE_STORY_PATHS.SideStory)
  const queryFn = match
    ? useGetSideStoryDetail
    : id === 'first-story'
      ? useGetFirstStory
      : useGetOpeningStoryDetail
  const { redirectTo: redirect } = useContext(HistoryRouteContext)
  const { data: stories } = queryFn(id)
  const muteSettings = {
    bgm: false,
    se: false,
    voice: false
  }
  useEffect(() => {
    localStorage.setItem('isStoryLoading', true)
  }, [])

  const [storyIndex, setStoryIndex] = useState(0)
  const [openHistory, setOpenHistory] = useState(false)
  const [isAuto, setIsAuto] = useState(false)
  const [isAllDisplayed, setIsAllDisplayed] = useState(false)
  const [blackoutColor, setBlackoutColor] = useState('black')
  const [isRepeatBgm, setIsRepeatBgm] = useState(false)
  const [audioSettings, setAudioSettings] = useState(muteSettings)
  const [isMusicModalOpen, setIsMusicModalOpen] = useState(false)
  const messagesEndRef = useRef(null)
  const ref = useRef(null)
  const audioRef = useRef(null)
  const audioSeRef = useRef(null)
  const wrapperRef = useRef(null)
  const containerRef = useRef(null)
  const audioCharacterRef = useRef(null)
  const navigate = useNavigate()
  const storyRef = useRef(storyIndex)
  const timerRef = useRef(null)
  const location = useLocation()
  const from = location.state?.from
  const currentStory = useMemo(() => (stories || [])[storyIndex], [stories, storyIndex])
  const nextStory = useMemo(() => (stories || [])[storyIndex + 1], [stories, storyIndex])
  const showBackGroundMusicNotif = useMemo(() => {
    if (!stories || !nextStory) {
      return false
    }
    if (nextStory.character_voice_url) {
      return true
    }
    if (nextStory.type === 'Blackout') {
      const laterStory = stories[storyIndex + 2]
      if (laterStory && laterStory.character_voice_url) {
        return true
      }
    }
    return false
  }, [stories, storyIndex, nextStory])
  const isLastStory = useMemo(() => {
    if (!stories) return true
    return storyIndex === stories.length - 1
  }, [stories, storyIndex])

  const [blackoutColorAnimation, setBlackoutColorAnimation] = useState('black')
  const storyDescArray = useMemo(() => {
    if (!stories) return
    return stories.map((story) => story.description)
  }, [stories])
  const [openModal, setOpenModal] = useState(false)
  useEffect(() => {
    if (currentStory || !isLastStory) {
      const elapsed = Date.now() - startTime.current
      const delay = Math.max(1300 - elapsed, 0)
      const timer = setTimeout(() => {
        setIsReady(true)
      }, delay)
      return () => clearTimeout(timer)
    } else {
      setIsReady(false)
    }
  }, [currentStory, isLastStory])
  useEffect(() => {
    if (!stories) return
    let settingBg = ''
    let settingName = ''
    let settingAddress = ''
    stories.forEach((story, i) => {
      const beforeStory = stories[i - 1]

      if (story.type === 'Blackout') {
        story.backgroundImage = beforeStory.backgroundImage
      } else if (beforeStory?.duringBlackout || beforeStory?.isBlackout) {
        story.duringBlackout = true
      } else {
        story.duringBlackout = false
      }

      if (story.type === 'StorySet') {
        if (story.bgmUrl) setIsRepeatBgm(story.isBgmAudioRepeat)
        if (!story.characterName) {
          story.characterName = settingName
        } else {
          settingName = story.characterName
        }
        if (!story.address) {
          story.address = settingAddress
        } else {
          settingAddress = story.address
        }

        if (!story.backgroundImage) {
          if (story.duringBlackout) return
          story.backgroundImage = settingBg
        } else {
          settingBg = story.backgroundImage
        }
      }
    })
  }, [stories])

  const redirectTo = useCallback(() => {
    const path = from === '/menu' ? MAIN_PATHS.MessagePage : MAIN_PATHS.MyPage
    redirect(path)
  }, [location])

  const pushCompleteOpeningEvent = useCallback(() => {
    window.dataLayer = window.dataLayer || []
    const payload = { event: 'complete_opening', transaction_id: `opening_${Date.now()}` }
    console.log('[GTM] push complete_opening', payload)
    window.dataLayer.push(payload)
  }, [])

  const handleHistoryAction = useCallback(() => {
    setOpenHistory(true)
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' })
  }, [setOpenHistory])

  useEffect(() => {
    storyRef.current = storyIndex
  }, [storyIndex])

  const handleAutoAction = useCallback(() => {
    if (isLastStory) return
    setIsAuto(!isAuto)
  }, [isAuto, storyIndex, stories, isLastStory])

  useEffect(() => {
    if (!currentStory) return
    if (currentStory?.isBlackout === undefined) {
      if (!wrapperRef?.current) return
      wrapperRef.current.style.opacity = 1
      return
    }
    if (currentStory.isBlackout) {
      if (storyIndex >= stories.length - 2 && !nextStory) {
        postReadOpeningStory()
        pushCompleteOpeningEvent()
        return redirectTo()
      }
      setTimeout(() => {
        setStoryIndex((prev) => prev + 1)
      }, currentStory.timeToChange + currentStory.timeToLeave)
    }
  }, [storyIndex, currentStory])

  useEffect(() => {
    if (!isReady) return
    setIsAllDisplayed(false)
    if (!ref.current || !currentStory) return
    ref.current.textContent = ''
    if (!currentStory.description) return
    const speed = 20
    if (isAuto) {
      const string = storyDescArray[storyIndex].split('')
      typewriterAuto(string, speed)
    } else {
      const string = currentStory.description.split('')
      typewriter(string, speed)
      controlSeAudio(string.length * speed)
    }
  }, [isReady, currentStory, isAuto, storyDescArray, openHistory])

  const typewriter = useCallback(
    (string, speed) => {
      const soundEffect = audioCharacterRef.current
      if (soundEffect && soundEffect.src && soundEffect.src.includes('mp3')) {
        soundEffect.play()
      }
      string.forEach((char, index) => {
        setTimeout(() => {
          if (!ref.current) return
          if (index !== ref.current.textContent.length) return
          ref.current.textContent += char
        }, speed * index)
      })
    },
    [storyIndex, stories, audioCharacterRef]
  )

  const handleBlackout = () => {
    let opacity = 1
    setBlackoutColorAnimation(nextStory.blackoutColor)
    const intervalId = setInterval(() => {
      opacity -= 0.1
      if (wrapperRef.current) {
        wrapperRef.current.style.opacity = opacity
      }
      if (opacity <= 0) {
        clearInterval(intervalId)
        setTimeout(() => {
          setStoryIndex((prev) => prev + 1)
        }, nextStory.timeToLeave)
      }
    }, nextStory.timeToChange / 10)
  }

  const typewriterAuto = useCallback(
    async (string, speed) => {
      if (ref.current) {
        const soundEffect = audioCharacterRef.current
        if (soundEffect && soundEffect.src && soundEffect.src.includes('mp3')) {
          string.forEach((char, index) => {
            setTimeout(() => {
              if (index !== ref.current.textContent.length) return
              ref.current.textContent += char
            }, speed * index)
          })
          soundEffect.play()
          soundEffect.addEventListener('ended', () => {
            if (string.length === ref.current.textContent.length) {
              if (isLastStory) return
              if (nextStory.isBlackout) {
                handleBlackout()
              } else {
                setStoryIndex((prev) => prev + 1)
              }
            }
          })
        } else {
          string.forEach((char, index) => {
            setTimeout(() => {
              if (index !== ref.current.textContent.length) return
              ref.current.textContent += char
              if (string.length === ref.current.textContent?.length) {
                if (isLastStory) return
                if (nextStory.isBlackout) {
                  let opacity = 1
                  setBlackoutColorAnimation(nextStory.blackoutColor)
                  const intervalId = setInterval(() => {
                    opacity -= 0.1
                    if (wrapperRef.current) {
                      wrapperRef.current.style.opacity = opacity
                    }
                    if (opacity <= 0) {
                      clearInterval(intervalId)
                      setStoryIndex((prev) => prev + 1)
                    }
                  }, nextStory.timeToChange / 10)
                } else {
                  setTimeout(() => setStoryIndex((prev) => prev + 1), 2000)
                }
              }
            }, speed * index)
          })
        }
      }
    },
    [storyIndex, stories, audioCharacterRef.current]
  )

  const controlSeAudio = useCallback((time) => {
    setTimeout(() => {
      setIsAllDisplayed(true)
    }, time)
  }, [])

  const blackoutBg = useMemo(() => {
    if (!currentStory) return
    if (currentStory.type === 'Blackout' || currentStory.type === 'CancelBlackout') {
      return blackoutColor
    }

    if (currentStory.duringBlackout) return 'black'
  }, [currentStory])

  useEffect(() => {
    if (!audioRef.current || !isReady) return
    audioRef.current.volume = 0.3
  }, [currentStory, isReady])

  useEffect(() => {
    if (!audioSeRef.current || !isReady) return
    audioSeRef.current.volume = 0.3
  }, [audioSeRef.current, isReady])

  const handleAudioClick = (type) => {
    setAudioSettings((prev) => ({
      ...prev,
      [type]: !prev[type]
    }))
  }

  const handleMusicModalToggle = () => {
    setIsMusicModalOpen((prev) => !prev)
  }

  useEffect(() => {
    if (stories && storyIndex > stories.length - 1) {
      postReadOpeningStory()
      pushCompleteOpeningEvent()
      redirectTo()
    }
  }, [storyIndex, stories])

  return {
    stories,
    currentStory,
    blackoutBg,
    isRepeatBgm,
    isAllDisplayed,
    openHistory,
    nextStory,
    audioSettings,
    handleHistoryAction,
    handleAutoAction,
    handleAudioClick,
    handleMusicModalToggle,
    redirectTo,
    ref,
    messagesEndRef,
    isLastStory,
    isAuto,
    isMusicModalOpen,
    setOpenHistory,
    setStoryIndex,
    setAudioSettings,
    setIsMusicModalOpen,
    storyIndex,
    audioRef,
    isReady,
    audioSeRef,
    audioCharacterRef,
    wrapperRef,
    containerRef,
    blackoutColorAnimation,
    setBlackoutColorAnimation,
    openModal,
    setOpenModal,
    handleBlackout,
    showBackGroundMusicNotif,
    pushCompleteOpeningEvent
  }
}
